/**
 * "Move to another person" — the registrar's correction for a link already
 * made (cca-yb68 R1, ADR-064 §9).
 *
 * The review queue only stops NEW wrong links. This is the way back for one
 * already made, and the registrar's ruling bounds it: until the application is
 * lodged with Police, and not after, because from lodgement the result writes
 * onto the linked person's CRM record.
 *
 * Two design rules it obeys:
 *  - **Discrete, not adjacent.** It is a link in the card's footer, never a
 *    button beside "Mark lodged". Moving a link is a correction; the primary
 *    action must stay the only thing that looks like the next step.
 *  - **One chooser.** It opens the SAME {@link IdentityChooser} the review
 *    queue uses, on the same payload from the same server shape, with the same
 *    two equal answers and the same inline confirmation — only the
 *    confirmation's sentences differ, because a move emails nobody and does
 *    not send the application anywhere.
 */

import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { apiFetch, apiPost } from './api';
import { IdentityChooser, type IdentityChoice } from './IdentityChooser';
import {
  RELINK,
  classifyRelinkError,
  relinkConfirmText,
  relinkToggleLabel,
  relinkedMessage,
  type CandidatesPayload,
  type RelinkFailure,
  type RelinkOutcomeKind,
  type TypedApplication,
} from './identity-review';

/** GET …/record/:id/relink-candidates — the shared candidates payload, plus
 *  the typed side of the comparison and the person it is linked to now. */
interface RelinkCandidatesResponse extends CandidatesPayload {
  recordId: string;
  status: string | null;
  currentContactId: string | null;
  typed: TypedApplication;
}

const BUSY_LABELS = { link: 'Moving…', create: 'Creating…' } as const;

/**
 * The refusals from which there is no way forward, whichever call met them.
 * Named as a set rather than checked inline in two places, so the GET and the
 * POST cannot drift on what "the move is over" means:
 *  - `already_lodged` — the registrar's own boundary (ADR-064 §9): from
 *    lodgement the Police result files onto the linked person;
 *  - `unavailable` — the typed application has been scrubbed (ADR-010), so the
 *    people on record can no longer be looked up for it;
 *  - `not_found` — the record is gone; the queue on screen is stale.
 * NOT here, deliberately: `record_changed` (choose again on fresh evidence),
 * `forbidden` (signing in again fixes it) and `failed` (retryable).
 */
const TERMINAL_REFUSALS = ['already_lodged', 'unavailable', 'not_found'] as const;
const isTerminal = (kind: RelinkOutcomeKind): boolean =>
  (TERMINAL_REFUSALS as readonly string[]).includes(kind);

export function RelinkControl({
  recordId,
  parishName,
  applicantName,
  onAttempt,
  onMoved,
  onRefused,
}: {
  recordId: string;
  parishName: string;
  /** Names the toggle, so three open controls on one list are three DIFFERENT
   *  controls to a screen reader rather than "Move to another person" thrice. */
  applicantName: string;
  /** A move is being attempted: clear whatever the shared status region is
   *  still saying about the LAST one. Without this, a move that fails leaves
   *  the previous move's success sentence standing beside its own error —
   *  two live regions telling different stories about the same press. */
  onAttempt?: () => void;
  /** Announce what happened and refresh the queue the row belongs to. */
  onMoved: (message: string) => void;
  /**
   * A refusal there is no way past, reported to the HOST as well as shown here
   * (Miranda S-1). Two of the three are facts about the LIST, not just about
   * this control: the lodgement queue holds only `Parish Approved Pending
   * Lodging`, so an `already_lodged` — or a `not_found` — is evidence the rows
   * on screen are stale, and that row's own "Mark lodged" would be refused too.
   * Without this the row sat there saying the application was already lodged
   * with an enabled "Mark lodged" beside it; both could not be true.
   */
  onRefused?: (kind: RelinkOutcomeKind) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [data, setData] = useState<RelinkCandidatesResponse | null>(null);
  const [busy, setBusy] = useState<'link' | 'create' | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * A refusal no retry can clear (B4/S-7): the server has told us this row can
   * never be moved — it is lodged with Police, what was typed has been scrubbed,
   * or the record is gone. The panel closes, the toggle is REPLACED by the
   * server's own sentence, and there is nothing left to press. A disabled
   * control would give no reason (Miranda §3c); this gives the reason and
   * nothing else.
   */
  const [refusal, setRefusal] = useState<string | null>(null);

  const openRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const refusalRef = useRef<HTMLParagraphElement | null>(null);
  /** True once the panel has been closed at least once, so focus is only
   *  taken back to the link after a real close — never on first render. */
  const closedOnce = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setData(
        await apiFetch<RelinkCandidatesResponse>(
          `/api/vetting/registrar/record/${recordId}/relink-candidates`,
        ),
      );
    } catch (err) {
      // The server's own words for a refusal it owns — `already_lodged` above
      // all, which is the one the registrar must be able to read. Anything
      // unclassified is a LOAD failure and says so: the move's own "couldn't
      // move this application" would be a lie about what was attempted.
      const failure = classifyRelinkError(err);
      if (isTerminal(failure.kind)) {
        // The SAME three refusals the move POST answers, and the same
        // treatment: a "Try again" button under a sentence that says the move
        // is impossible is a button that can never succeed.
        refuse(failure);
        return;
      }
      setLoadError(failure.kind === 'failed' ? RELINK.loadError : failure.message);
      setData(null);
    } finally {
      setLoading(false);
    }
    // `refuse` is a hoisted declaration below; it only calls setState and the
    // `onRefused` prop, neither of which cares which render it was created in
    // (the host's handler names this row by its record id), so it is safe to
    // close over and is not a dependency.
  }, [recordId]);

  /**
   * Close the panel and DROP the candidates (Miranda S-1).
   *
   * The payload carries a `version` the server uses to refuse a stale set, and
   * the names it shows include "Linked now". Keeping either across a close —
   * or across a successful move, which does not unmount this component because
   * the row's key is the record id — meant a reopen re-showed the pre-move
   * contact and re-quoted a version that had already been spent. The reopen
   * below therefore refetches unconditionally; a saved request is not worth a
   * decision made against last minute's evidence.
   */
  function close() {
    closedOnce.current = true;
    setOpen(false);
    setError(null);
    setLoadError(null);
    setData(null);
  }

  /**
   * A terminal refusal: close everything, keep the server's sentence, and tell
   * the host which refusal it was — the row it belongs to has its own control
   * to take off the table when the refusal means the queue is stale (S-1).
   */
  function refuse(failure: RelinkFailure) {
    close();
    setRefusal(failure.message);
    onRefused?.(failure.kind);
  }

  useEffect(() => {
    if (!open && closedOnce.current && !refusal) openRef.current?.focus();
  }, [open, refusal]);

  // A terminal refusal destroys the control that was pressed AND the toggle it
  // would have returned focus to, so focus is placed on the sentence that
  // replaced them rather than dropped to the top of the dashboard.
  useEffect(() => {
    if (refusal) refusalRef.current?.focus();
  }, [refusal]);

  async function choose(choice: IdentityChoice) {
    setBusy(choice.action);
    setError(null);
    onAttempt?.();
    const crmName =
      choice.action === 'link'
        ? (data?.candidates.find((cnd) => cnd.contactId === choice.contactId)?.name ?? null)
        : null;
    try {
      const res = await apiPost<{ status: string; drive?: string | null; auditLogged?: boolean }>(
        `/api/vetting/registrar/record/${recordId}/relink`,
        {
          action: choice.action,
          ...(choice.contactId ? { contactId: choice.contactId } : {}),
          version: choice.version,
        },
      );
      // `close()`, not a bare setOpen(false): it drops the candidates with the
      // panel. The move has just changed who is linked, so every name and the
      // `version` in `data` describe the world BEFORE it — and this component
      // is not unmounted by the queue reload (the row key is the record id),
      // so without this the next open showed the person he moved away from as
      // "Linked now" (Miranda S-1).
      close();
      onMoved(
        res.status === 'noop'
          ? 'That application is already on that person. Nothing changed.'
          : relinkedMessage({
              action: choice.action,
              typedName: data?.typed.applicantName ?? 'this applicant',
              crmName,
              drive: res.drive ?? null,
              auditLogged: res.auditLogged,
            }),
      );
    } catch (err) {
      const failure = classifyRelinkError(err);
      // Every refusal the relink route can answer, each disposed of once.
      // Before this, all of them but `record_changed` merely set an error
      // string — which left the confirmation open with a filled "Yes, move to
      // …" that could never succeed (Miranda S-7).
      if (isTerminal(failure.kind)) {
        // already_lodged · unavailable · not_found — over for this row. Closing
        // the panel takes the confirmation and its filled button with it; the
        // server's sentence replaces the toggle, so the move is not offered
        // again on a row that cannot take it.
        refuse(failure);
      } else if (failure.kind === 'record_changed') {
        // The people on that email moved: show the CURRENT ones and let the
        // registrar choose again. The stale choice is dropped, never resent.
        // The chooser closes its own confirmation when the version changes.
        if (failure.fresh && data) setData({ ...data, ...failure.fresh });
        setError(failure.message);
      } else {
        // forbidden (sign in again) · failed (try again) — both recoverable
        // without leaving the row, so the panel and the evidence stay put and
        // the sentence is announced inside the card.
        setError(failure.message);
      }
    } finally {
      setBusy(null);
    }
  }

  // A refusal there is no way past: the toggle is GONE, replaced by the reason.
  if (refusal) {
    return (
      <div className="idr-relink">
        <p className="idr-note" role="alert" tabIndex={-1} ref={refusalRef}>
          {refusal}
        </p>
      </div>
    );
  }

  return (
    <div className="idr-relink">
      <button
        type="button"
        className="idr-link"
        ref={openRef}
        aria-expanded={open}
        aria-controls={`relink-${recordId}`}
        aria-label={relinkToggleLabel(applicantName)}
        onClick={() => {
          if (open) {
            close();
            return;
          }
          setOpen(true);
          // Unconditional, not `if (!data)`: `close()` has already dropped the
          // candidates, and every open must quote a `version` the server will
          // still accept (Miranda S-1).
          void load();
        }}
      >
        {RELINK.open}
      </button>

      {open && (
        <div className="idr-card" id={`relink-${recordId}`} ref={panelRef}>
          {/* A heading, so it is one: it was a styled <p> that read as a
              heading to sighted users and as body text to everybody else.
              h3 — the sections of both pages that mount this are h2
              (RegistrarDashboardPage's SectionHead/CollapsibleHead and
              VettingQueuePage's), so the outline runs h1 → h2 → this. */}
          <h3 className="idr-paper-head">{RELINK.heading}</h3>
          <p className="idr-paper-body">{RELINK.body}</p>
          <IdentityChooser
            typed={
              data?.typed ?? {
                applicantName: '—',
                applicantEmail: '—',
                dateOfBirthSubmitted: '—',
                jobTitle: '—',
                townCity: '—',
              }
            }
            parishName={parishName}
            payload={data}
            loading={loading}
            loadError={loadError}
            onRetryLoad={() => void load()}
            busy={busy}
            error={error}
            confirmCopy={relinkConfirmText}
            busyLabels={BUSY_LABELS}
            currentContactId={data?.currentContactId ?? null}
            onChoose={(choice) => void choose(choice)}
            // The way out of a panel opened by mistake (Miranda S-8): the
            // chooser renders it as "Leave it as it is" beside Create in the
            // single-candidate dead end, and calls it on Escape when no
            // confirmation is open.
            //
            // SCOPED HERE, not in the chooser: several of these panels can be
            // open on one queue, and an unscoped document-level Escape would
            // close every one of them and leave focus on whichever toggle
            // happened to win the race. Focus is on the toggle immediately
            // after opening, so both it and the panel count as "in here".
            onDismiss={() => {
              const active = document.activeElement;
              if (active === openRef.current || panelRef.current?.contains(active)) close();
            }}
          />
        </div>
      )}
    </div>
  );
}
